import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext(undefined);

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [notificationsApiAvailable, setNotificationsApiAvailable] = useState(true);

  useEffect(() => {
    if (currentUser?.id) {
      fetchNotifications();
    }
  }, [currentUser]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/${currentUser.id}`);
      setNotifications(res.data);
      setNotificationsApiAvailable(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotificationsApiAvailable(false);
        setNotifications([]);
        return;
      }

      console.error('Error fetching notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = async (title, message, type = 'info') => {
    if (!currentUser?.id) {
      return;
    }

    if (!notificationsApiAvailable) {
      const newNotif = {
        id: Date.now().toString(),
        userId: currentUser.id,
        title,
        message,
        type,
        timestamp: new Date().toISOString(),
        read: false
      };
      setNotifications((prev) => [newNotif, ...prev]);
      return;
    }

    try {
      const res = await axios.post(`${API_URL}/api/notifications`, {
        userId: currentUser.id,
        title,
        message,
        type
      });
      setNotifications((prev) => [res.data, ...prev]);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotificationsApiAvailable(false);
      } else {
        console.error('Error adding notification:', error);
      }

      const newNotif = {
        id: Date.now().toString(),
        userId: currentUser.id,
        title,
        message,
        type,
        timestamp: new Date().toISOString(),
        read: false
      };
      setNotifications((prev) => [newNotif, ...prev]);
    }
  };

  const markAsRead = async (id) => {
    try {
      if (notificationsApiAvailable) {
        await axios.put(`${API_URL}/api/notifications/${id}/read`);
      }
      setNotifications((prev) => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      if (error.response?.status === 404) {
        setNotificationsApiAvailable(false);
      }
      setNotifications((prev) => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }
  };

  const markAllAsRead = async () => {
    try {
      if (notificationsApiAvailable) {
        await axios.put(`${API_URL}/api/notifications/user/${currentUser.id}/read-all`);
      }
      setNotifications((prev) => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      if (error.response?.status === 404) {
        setNotificationsApiAvailable(false);
      }
      setNotifications((prev) => prev.map(n => ({ ...n, read: true })));
    }
  };

  const clearNotifications = async () => {
    try {
      if (notificationsApiAvailable) {
        await axios.delete(`${API_URL}/api/notifications/user/${currentUser.id}`);
      }
      setNotifications([]);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotificationsApiAvailable(false);
      }
      setNotifications([]);
    }
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      clearNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotification must be used within a NotificationProvider");
  return context;
};
